import { useEffect, useState, useCallback } from "react";
import { Check, X, Ban } from "lucide-react";
import { api, errMsg, fmtDate, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Empty, statusBadge, statusLabel } from "@/admin/ui";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const FILTERS = [["pending", "Bekleyen"], ["approved", "Onaylı"], ["rejected", "Reddedilen"], ["", "Tümü"]];

export function AdminPayments() {
  const { site } = useAuth();
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const load = useCallback(() => api.get("/admin/payments", { params: filter ? { status: filter } : {} }).then((r) => setRows(r.data)).catch(() => setRows([])), [filter]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const approve = async (p) => {
    try { await api.post(`/admin/payments/${p.id}/approve`); toast.success(`${p.username} için içerik açıldı`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async (ban) => {
    const p = rejecting; setRejecting(null);
    try { await api.post(`/admin/payments/${p.id}/reject`, { ban }); toast.success(ban ? `${p.username} banlandı` : "Ödeme reddedildi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <SectionTitle eyebrow="Ödeme Sistemi" title="Ödeme Bildirimleri">
        <div className="flex gap-1 p-1 rounded-xl bg-slate-950/70 border border-slate-800">
          {FILTERS.map(([v, l]) => (
            <button key={v} data-testid={`payments-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`h-9 px-3 rounded-lg text-xs font-semibold transition-colors ${filter === v ? "bg-slate-800 text-cyan-300" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      </SectionTitle>
      {rows === null ? <div className="h-40 rounded-2xl bg-slate-900/60 animate-pulse" /> : rows.length === 0 ? <Empty text="Bu filtrede ödeme bildirimi yok." /> : (
        <div className="cx-glass rounded-2xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-payments-table">
            <thead className="text-[11px] font-mono-cx uppercase tracking-widest text-slate-500 border-b border-slate-800">
              <tr><th className="text-left px-5 py-3">Kullanıcı</th><th className="text-left px-5 py-3">Bilgi</th><th className="text-left px-5 py-3">Tutar</th><th className="text-left px-5 py-3">IP</th><th className="text-left px-5 py-3">Tarih</th><th className="text-left px-5 py-3">Durum</th><th className="px-5 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {rows.map((p) => (
                <tr key={p.id} data-testid="admin-payment-row">
                  <td className="px-5 py-3 font-semibold text-slate-100">{p.username}</td>
                  <td className="px-5 py-3 text-slate-300">{p.info_title}</td>
                  <td className="px-5 py-3 text-cyan-300 font-display font-bold">{fmtMoney(p.amount, site?.currency)}</td>
                  <td className="px-5 py-3 font-mono-cx text-xs text-slate-400">{p.ip}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(p.created_at)}</td>
                  <td className="px-5 py-3"><span className={`text-[10px] font-mono-cx px-2 py-1 rounded-md border ${statusBadge(p.status)}`}>{statusLabel(p.status)}{p.banned_user ? " · BAN" : ""}</span></td>
                  <td className="px-5 py-3">
                    {p.status === "pending" && (
                      <div className="flex gap-2 justify-end">
                        <button data-testid="admin-approve-payment-button" onClick={() => approve(p)} className="h-8 px-3 rounded-lg bg-emerald-950 border border-emerald-500/50 text-emerald-300 text-xs font-semibold inline-flex items-center gap-1 hover:bg-emerald-900 transition-colors"><Check className="h-3.5 w-3.5" /> Yaptı</button>
                        <button data-testid="admin-reject-payment-button" onClick={() => setRejecting(p)} className="h-8 px-3 rounded-lg bg-red-950 border border-red-500/50 text-red-300 text-xs font-semibold inline-flex items-center gap-1 hover:bg-red-900 transition-colors"><X className="h-3.5 w-3.5" /> Yapmadı</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <AlertDialogContent className="bg-slate-950/95 backdrop-blur-xl border-slate-800 text-slate-100" data-testid="reject-payment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Ödeme yapılmadı — kullanıcı banlansın mı?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              <b className="text-slate-100">{rejecting?.username}</b> ({rejecting?.ip}) ödeme yapmadığı halde "Gönderdim" dedi. Banlarsan hesabı ve IP adresi kalıcı olarak engellenir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="reject-no-ban-button" onClick={() => reject(false)} className="bg-slate-800 text-slate-100 hover:bg-slate-700">Sadece Reddet</AlertDialogAction>
            <AlertDialogAction data-testid="admin-ban-user-button" onClick={() => reject(true)} className="bg-red-600 hover:bg-red-500 text-white"><Ban className="h-4 w-4 mr-1" /> Reddet ve Banla</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
